import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';
import * as React from 'react';
import { Divider, DividerProps } from '../components';

const paperVariants = cva('bg-background-input rounded-lg', {
  variants: {
    // Use `none` when Paper sits inside an already-padded container (e.g. a
    // settings Card) so the inner padding isn't doubled.
    padding: {
      lg: 'p-lg',
      none: 'p-0'
    },
    heading: {
      default: 'gap-sm',
      stripe: 'gap-md'
    }
  },
  compoundVariants: [
    // A stripe section inside a card draws no surface of its own, only the stripe.
    { heading: 'stripe', padding: 'none', class: 'bg-transparent rounded-none' }
  ],
  defaultVariants: {
    padding: 'lg',
    heading: 'default'
  }
});

const stripeVariants = cva(
  [
    '[grid-area:header] flex flex-wrap items-center gap-md py-sm',
    'bg-secondary-base/10 border-y border-border-base border-l-4 border-l-secondary-base'
  ],
  {
    variants: {
      // Bleeding cancels the parent's `lg` padding and keeps the title in line with the content.
      bleed: {
        true: '-mx-lg px-lg',
        false: 'px-md'
      }
    },
    defaultVariants: {
      bleed: false
    }
  }
);

type PaperVariants = Omit<VariantProps<typeof paperVariants>, 'heading'>;

/** How the title and the action are drawn. */
export type PaperHeading = 'default' | 'stripe';

const PaperHeadingContext = React.createContext<PaperHeading>('default');

export interface PaperProps extends PaperVariants, DividerProps {
  className?: string;
  children?: React.ReactNode;
  showDivider?: boolean;
  /** `stripe` draws the title and the action in one tinted full-width stripe, with no divider. */
  heading?: PaperHeading;
  /** Lets the stripe reach over the parent's `lg` padding to its edges. */
  bleed?: boolean;
  testId?: string;
}

export interface PaperTitleProps {
  className?: string;
  children?: React.ReactNode;
  /** Sets the title in capitals with wider tracking. */
  uppercase?: boolean;
}

export interface PaperActionProps {
  className?: string;
  children?: React.ReactNode;
}

export interface PaperContentProps {
  className?: string;
  children?: React.ReactNode;
}

const displayNameOf = (child: unknown): string | undefined =>
  React.isValidElement(child) ? (child.type as { displayName?: string })?.displayName : undefined;

const isTitleOrAction = (child: unknown): boolean => {
  const displayName = displayNameOf(child);
  return displayName === 'Paper.Title' || displayName === 'Paper.Action';
};

export function Paper(props: PaperProps) {
  const {
    className,
    children,
    showDivider = true,
    size = 'full',
    thickness = 'sm',
    variant = 'light',
    padding,
    heading = 'default',
    bleed = false,
    testId
  } = props;

  const childrenArray = React.Children.toArray(children);

  const titleOrActionChildren = childrenArray.filter(isTitleOrAction);

  const otherChildren = childrenArray.filter(
    child => React.isValidElement(child) && !isTitleOrAction(child)
  );

  const hasTitleOrAction = titleOrActionChildren.length > 0;
  const isStripe = heading === 'stripe';

  return (
    <PaperHeadingContext.Provider value={heading}>
      <section
        data-testid={testId}
        className={cn(
          'grid content-start',
          isStripe
            ? "grid-cols-1 [grid-template-areas:'header'_'content']"
            : "grid-cols-[1fr_auto] [grid-template-areas:'title_action'_'divider_divider'_'content_content']",
          paperVariants({ padding, heading, className })
        )}
      >
        {isStripe ? (
          hasTitleOrAction && (
            <div className={stripeVariants({ bleed })} data-slot='paper-stripe'>
              {titleOrActionChildren}
            </div>
          )
        ) : (
          <>
            {titleOrActionChildren.map((child, index) => (
              <React.Fragment key={`ta-${index}`}>{child}</React.Fragment>
            ))}

            {showDivider && hasTitleOrAction && (
              <div className='[grid-area:divider] '>
                <Divider size={size} variant={variant} thickness={thickness} className='mt-[5px]' />
              </div>
            )}
          </>
        )}

        {otherChildren.map((child, index) => (
          <React.Fragment key={`other-${index}`}>{child}</React.Fragment>
        ))}
      </section>
    </PaperHeadingContext.Provider>
  );
}

Paper.Title = ((props: PaperTitleProps) => {
  const { children, className, uppercase = false } = props;
  const heading = React.useContext(PaperHeadingContext);
  return (
    <div
      className={cn(
        heading === 'stripe'
          ? 'text-sm font-semibold text-foreground-base'
          : 'py-sm text-xl [grid-area:title]',
        uppercase && 'uppercase tracking-wider',
        className
      )}
    >
      {children}
    </div>
  );
}) as React.FC<PaperTitleProps>;
Paper.Title.displayName = 'Paper.Title';

Paper.Action = ((props: PaperActionProps) => {
  const { children, className } = props;
  const heading = React.useContext(PaperHeadingContext);
  return (
    <div
      className={cn(
        heading === 'stripe' ? 'flex items-center' : 'py-sm [grid-area:action]',
        className
      )}
    >
      {children}
    </div>
  );
}) as React.FC<PaperActionProps>;
Paper.Action.displayName = 'Paper.Action';

Paper.Content = ((props: PaperContentProps) => {
  const { children, className } = props;
  return <div className={cn('[grid-area:content]', className)}>{children}</div>;
}) as React.FC<PaperContentProps>;
Paper.Content.displayName = 'Paper.Content';
